import { useEffect, useMemo, useState } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
    GetAllGrouping,
    GetGroupingById,
    GetGroupingByName,
    AddGrouping,
    UpdateGrouping,
    RemoveGrouping,
} from "../services/GroupingService";
// فرض: سرویس‌های محصول/مشتری/فروشنده مشابه سرویس گروه‌بندی، یک تابع برای گرفتن همه‌ی آیتم‌ها دارند.
// اگر اسم فایل/تابع در پروژه‌ی شما فرق دارد، فقط همین importها را اصلاح کنید.
import { GetActiveProduct } from "../services/ProductService";
import { GetAllCustomer } from "../services/CustomerService";
import { GetAllEmployee } from "../services/EmployeeService";
import { useLanguage } from "./LanguageContext"; // مسیر واقعی خودت

/* -------------------------------------------------------------------------- */
/*  فونت پایه                                                                  */
/* -------------------------------------------------------------------------- */
const FONT = "IRANSansWeb, Vazirmatn, sans-serif";

/* -------------------------------------------------------------------------- */
/*  رنگ/آیکون مخصوص «آیتم متصل» (برگ‌های مشتری/فروشنده/محصول)                   */
/*  تا با گره‌های گروه‌بندی معمولی قاطی نشود                                    */
/* -------------------------------------------------------------------------- */
const ENTITY_LEAF_COLOR = "#0EA5A5";
const ENTITY_LEAF_ICON = "tag";

/* -------------------------------------------------------------------------- */
/*  متن‌ها                                                                     */
/* -------------------------------------------------------------------------- */
const TEXTS = {
    fa: {
        dir: "rtl",
        title: "مدیریت گروه‌بندی‌ها",
        subtitle: "ساختار درختی گروه‌ها را مدیریت کنید",
        searchPlaceholder: "جستجوی نام گروه...",
        addRoot: "افزودن گروه ریشه",
        addChild: "افزودن زیرگروه",
        addChildDisabledHint: "این شاخه به‌طور مستقیم آیتم متصل دارد و امکان افزودن زیرگروه دیگر به آن نیست",
        addEntityDisabledHint: "این گروه دارای زیرگروه است و امکان افزودن مستقیم آیتم به آن وجود ندارد",
        edit: "ویرایش",
        delete: "حذف",
        name: "نام گروه",
        type: "نوع",
        parent: "والد",
        none: "بدون والد (ریشه)",
        save: "ذخیره",
        cancel: "انصراف",
        modalAddTitle: "افزودن گروه جدید",
        modalEditTitle: "ویرایش گروه",
        confirmDeleteTitle: "حذف گروه",
        confirmDeleteMsg: (name) => `آیا از حذف «${name}» مطمئن هستید؟`,
        hasChildrenHint: "این گروه زیرمجموعه دارد و قابل حذف نیست",
        entityLeafHint: "این یک آیتم متصل‌شده است و امکان افزودن زیرمجموعه به آن وجود ندارد",
        yesDelete: "بله، حذف کن",
        cancelDelete: "انصراف",
        loading: "در حال بارگذاری...",
        saving: "در حال ذخیره...",
        deleting: "در حال حذف...",
        empty: "هنوز هیچ گروهی در این دسته ثبت نشده است",
        emptyAction: "افزودن اولین گروه",
        noSearchResult: "نتیجه‌ای یافت نشد",
        rootBadge: "ریشه",
        toastNameRequired: "نام گروه را وارد کنید",
        toastAddSuccess: "گروه با موفقیت افزوده شد",
        toastAddFail: "خطا در افزودن گروه",
        toastUpdateSuccess: "گروه با موفقیت ویرایش شد",
        toastUpdateFail: "خطا در ویرایش گروه",
        toastDeleteSuccess: "گروه با موفقیت حذف شد",
        toastDeleteFail: "خطا در حذف گروه (ممکن است زیرمجموعه داشته باشد)",
        toastFetchFail: "خطا در دریافت لیست گروه‌ها",
        toastSearchFound: "گروه مورد نظر پیدا و در درخت نمایش داده شد",
        toastSearchNotFound: "گروهی با این نام پیدا نشد",
        toastSearchFail: "خطا در جستجو",
        itemsCount: (n) => `${n} گروه`,
        categoryHint: "این گروه در دسته",
        categoryHintSuffix: "ثبت می‌شود",
        exportExcel: "خروجی اکسل",
        exportBranch: "خروجی اکسل این شاخه",
        toastExportSuccess: "فایل اکسل با موفقیت ساخته شد",
        toastExportFail: "خطا در ساخت فایل اکسل",
        toastExportEmpty: "داده‌ای برای خروجی گرفتن وجود ندارد",
        colIndex: "ردیف",
        colName: "نام گروه",
        colLevel: "سطح",
        colParent: "گروه والد",
        colEntityName: "آیتم متصل",
        colChildrenCount: "تعداد زیرگروه",
        entityAlreadyUsed: "قبلاً در این دسته استفاده شده",
        toastAttachDuplicate: "این آیتم قبلاً در همین شاخه اصلی به گروه دیگری متصل شده است",
    },
    ar: {
        dir: "rtl",
        title: "إدارة التصنيفات",
        subtitle: "إدارة الهيكل الشجري للمجموعات",
        searchPlaceholder: "بحث باسم المجموعة...",
        addRoot: "إضافة مجموعة رئيسية",
        addChild: "إضافة مجموعة فرعية",
        addChildDisabledHint: "هذا الفرع يحتوي مباشرة على عنصر مرتبط ولا يمكن إضافة مجموعة فرعية أخرى إليه",
        addEntityDisabledHint: "هذه المجموعة تحتوي على مجموعات فرعية ولا يمكن إضافة عنصر مباشرة إليها",
        edit: "تعديل",
        delete: "حذف",
        name: "اسم المجموعة",
        type: "النوع",
        parent: "الأصل",
        none: "بدون أصل (رئيسي)",
        save: "حفظ",
        cancel: "إلغاء",
        modalAddTitle: "إضافة مجموعة جديدة",
        modalEditTitle: "تعديل المجموعة",
        confirmDeleteTitle: "حذف المجموعة",
        confirmDeleteMsg: (name) => `هل أنت متأكد من حذف «${name}»؟`,
        hasChildrenHint: "هذه المجموعة تحتوي على عناصر فرعية ولا يمكن حذفها",
        entityLeafHint: "هذا عنصر مرتبط ولا يمكن إضافة عناصر فرعية إليه",
        yesDelete: "نعم، احذف",
        cancelDelete: "إلغاء",
        loading: "جارٍ التحميل...",
        saving: "جارٍ الحفظ...",
        deleting: "جارٍ الحذف...",
        empty: "لم يتم تسجيل أي مجموعة في هذه الفئة بعد",
        emptyAction: "إضافة أول مجموعة",
        noSearchResult: "لا توجد نتائج",
        rootBadge: "رئيسي",
        toastNameRequired: "أدخل اسم المجموعة",
        toastAddSuccess: "تمت إضافة المجموعة بنجاح",
        toastAddFail: "خطأ في إضافة المجموعة",
        toastUpdateSuccess: "تم تعديل المجموعة بنجاح",
        toastUpdateFail: "خطأ في تعديل المجموعة",
        toastDeleteSuccess: "تم حذف المجموعة بنجاح",
        toastDeleteFail: "خطأ في حذف المجموعة (قد تحتوي على عناصر فرعية)",
        toastFetchFail: "خطأ في جلب قائمة المجموعات",
        toastSearchFound: "تم العثور على المجموعة وعرضها في الشجرة",
        toastSearchNotFound: "لم يتم العثور على مجموعة بهذا الاسم",
        toastSearchFail: "خطأ في البحث",
        itemsCount: (n) => `${n} مجموعة`,
        categoryHint: "سيتم تسجيل هذه المجموعة في فئة",
        categoryHintSuffix: "",
        exportExcel: "تصدير إكسل",
        exportBranch: "تصدير إكسل لهذا الفرع",
        toastExportSuccess: "تم إنشاء ملف إكسل بنجاح",
        toastExportFail: "خطأ في إنشاء ملف إكسل",
        toastExportEmpty: "لا توجد بيانات للتصدير",
        colIndex: "الرقم",
        colName: "اسم المجموعة",
        colLevel: "المستوى",
        colParent: "المجموعة الأصل",
        colEntityName: "العنصر المرتبط",
        colChildrenCount: "عدد المجموعات الفرعية",
        entityAlreadyUsed: "مستخدم مسبقاً في هذه الفئة",
        toastAttachDuplicate: "هذا العنصر مرتبط مسبقاً بمجموعة أخرى ضمن نفس الفرع الرئيسي",
    },
};

/* بر اساس Ordering.DataLayout.Enum.Grouping.GroupingTypeEnum */
const GROUPING_TYPE_OPTIONS = [
    { value: 1, label: { fa: "مشتریان", ar: "العملاء" }, icon: "users", color: "#6C3DFF" },
    { value: 2, label: { fa: "کاربران", ar: "المستخدمون" }, icon: "store", color: "#F59E0B" },
    { value: 3, label: { fa: "محصولات", ar: "المنتجات" }, icon: "box", color: "#10B981" },
];
const getTypeMeta = (value) =>
    GROUPING_TYPE_OPTIONS.find((o) => o.value === Number(value)) || {
        label: { fa: "-", ar: "-" },
        icon: "folder",
        color: "#94A3B8",
    };

/* -------------------------------------------------------------------------- */
/*  تنظیمات «افزودن آیتم» به تفکیک نوع گروه‌بندی                                */
/*  هر نوع (مشتری/فروشنده/محصول) سرویس، فیلد شناسه و متن‌های مخصوص خودش را دارد  */
/* -------------------------------------------------------------------------- */
const ENTITY_BY_TYPE = {
    1: {
        // مشتریان
        idField: "customerId",
        fetch: GetAllCustomer,
        fa: {
            addLabel: "افزودن مشتری",
            modalTitle: "افزودن مشتری به گروه",
            placeholder: "جستجوی مشتری...",
            loading: "در حال بارگذاری مشتریان...",
            empty: "مشتری‌ای یافت نشد",
            selectHint: "یک مشتری را برای افزودن به این گروه انتخاب کنید",
            toastSuccess: "مشتری با موفقیت افزوده شد",
            toastFail: "خطا در افزودن مشتری",
            toastFetchFail: "خطا در دریافت لیست مشتریان",
        },
        ar: {
            addLabel: "إضافة عميل",
            modalTitle: "إضافة عميل إلى المجموعة",
            placeholder: "بحث عن عميل...",
            loading: "جارٍ تحميل العملاء...",
            empty: "لم يتم العثور على عميل",
            selectHint: "اختر عميلاً لإضافته إلى هذه المجموعة",
            toastSuccess: "تمت إضافة العميل بنجاح",
            toastFail: "خطأ في إضافة العميل",
            toastFetchFail: "خطأ في جلب قائمة العملاء",
        },
    },
    2: {
        // کاربران
        idField: "employeeId",
        fetch: GetAllEmployee,
        fa: {
            addLabel: "افزودن کاربر",
            modalTitle: "افزودن کاربر به گروه",
            placeholder: "جستجوی کاربر...",
            loading: "در حال بارگذاری کاربران...",
            empty: "کاربری یافت نشد",
            selectHint: "یک کاربر را برای افزودن به این گروه انتخاب کنید",
            toastSuccess: "کاربر با موفقیت افزوده شد",
            toastFail: "خطا در افزودن کاربر",
            toastFetchFail: "خطا در دریافت لیست کاربران",
        },
        ar: {
            addLabel: "إضافة مستخدم",
            modalTitle: "إضافة مستخدم إلى المجموعة",
            placeholder: "بحث عن مستخدم...",
            loading: "جارٍ تحميل المستخدمين...",
            empty: "لم يتم العثور على مستخدم",
            selectHint: "اختر مستخدماً لإضافته إلى هذه المجموعة",
            toastSuccess: "تمت إضافة المستخدم بنجاح",
            toastFail: "خطأ في إضافة المستخدم",
            toastFetchFail: "خطأ في جلب قائمة المستخدمين",
        },
    },
    3: {
        // محصولات
        idField: "productId",
        fetch: GetActiveProduct,
        fa: {
            addLabel: "افزودن محصول",
            modalTitle: "افزودن محصول به گروه",
            placeholder: "جستجوی محصول...",
            loading: "در حال بارگذاری محصولات...",
            empty: "محصولی یافت نشد",
            selectHint: "یک محصول را برای افزودن به این گروه انتخاب کنید",
            toastSuccess: "محصول با موفقیت افزوده شد",
            toastFail: "خطا در افزودن محصول",
            toastFetchFail: "خطا در دریافت لیست محصولات",
        },
        ar: {
            addLabel: "إضافة منتج",
            modalTitle: "إضافة منتج إلى المجموعة",
            placeholder: "بحث عن منتج...",
            loading: "جارٍ تحميل المنتجات...",
            empty: "لم يتم العثور على منتج",
            selectHint: "اختر منتجاً لإضافته إلى هذه المجموعة",
            toastSuccess: "تمت إضافة المنتج بنجاح",
            toastFail: "خطأ في إضافة المنتج",
            toastFetchFail: "خطأ في جلب قائمة المنتجات",
        },
    },
};
const getEntityMeta = (type, lang) => {
    const cfg = ENTITY_BY_TYPE[Number(type)];
    if (!cfg) return null;
    return { idField: cfg.idField, fetch: cfg.fetch, t: cfg[lang] || cfg.fa };
};

/* عنوان ستون آخر در خروجی اکسل، بسته به نوع تب فعال (مشتری/فروشنده/محصول) */
const ENTITY_COLUMN_LABEL = {
    1: { fa: "نام مشتری", ar: "اسم العميل" },
    2: { fa: "نام کاربر", ar: "اسم المستخدم" },
    3: { fa: "نام محصول", ar: "اسم المنتج" },
};

/* -------------------------------------------------------------------------- */
/*  آیکن‌ها                                                                    */
/* -------------------------------------------------------------------------- */
function Icon({ name, size = 14, color = "currentColor" }) {
    const paths = {
        plus: <><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>,
        edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></>,
        trash: <><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /></>,
        "chevron-down": <polyline points="6 9 12 15 18 9" />,
        search: <><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>,
        close: <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>,
        users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
        store: <><path d="M3 9l1-5h16l1 5" /><path d="M4 9v10a1 1 0 0 0 1 1h4v-6h6v6h4a1 1 0 0 0 1-1V9" /><path d="M3 9a2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0" /></>,
        box: <><path d="M21 8l-9-5-9 5 9 5 9-5z" /><path d="M3 8v8l9 5 9-5V8" /><path d="M12 13v8" /></>,
        folder: <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />,
        check: <polyline points="20 6 9 17 4 12" />,
        alert: <><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></>,
        tag: <><path d="M20.59 13.41 11 3.83A2 2 0 0 0 9.59 3.24L4 3a1 1 0 0 0-1 1l.24 5.59a2 2 0 0 0 .59 1.41l9.58 9.59a2 2 0 0 0 2.83 0l4.35-4.35a2 2 0 0 0 0-2.83Z" /><circle cx="7.5" cy="7.5" r="1.2" fill="currentColor" stroke="none" /></>,
        download: <><path d="M12 3v12" /><polyline points="7 11 12 16 17 11" /><path d="M5 21h14" /></>,
        lock: <><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>,
        more: <><circle cx="5" cy="12" r="1.8" fill="currentColor" stroke="none" /><circle cx="12" cy="12" r="1.8" fill="currentColor" stroke="none" /><circle cx="19" cy="12" r="1.8" fill="currentColor" stroke="none" /></>,
    };
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
            fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            {paths[name] ?? null}
        </svg>
    );
}

function Toast({ message, visible, type }) {
    if (!visible) return null;
    return (
        <div style={{
            position: "fixed", bottom: 32, left: "50%", transform: "translateX(-50%)",
            background: type === "error" ? "#EF4444" : "#10B981",
            color: "#fff", padding: "10px 20px", borderRadius: 12,
            fontSize: 12, fontWeight: 500, zIndex: 2000,
            display: "flex", alignItems: "center", gap: 8,
            fontFamily: FONT,
            boxShadow: type === "error" ? "0 8px 24px rgba(239,68,68,.3)" : "0 8px 24px rgba(16,185,129,.3)",
        }}>
            <Icon name={type === "error" ? "alert" : "check"} size={13} color="#fff" />
            {message}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  فیلدهای فرم                                                                */
/* -------------------------------------------------------------------------- */
const fieldBaseStyle = {
    width: "100%", height: 40, border: "1.5px solid #E8ECF4",
    borderRadius: 10, padding: "0 12px", fontSize: 12,
    fontFamily: FONT, direction: "rtl",
    outline: "none", color: "#1E293B", boxSizing: "border-box",
    background: "#F8FAFF", transition: "all .15s",
};

function TextField({ value, onChange, placeholder, autoFocus }) {
    return (
        <input
            autoFocus={autoFocus}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            style={fieldBaseStyle}
            onFocus={(e) => { e.target.style.borderColor = "#6C3DFF"; e.target.style.background = "#fff"; e.target.style.boxShadow = "0 0 0 4px rgba(108,61,255,.08)"; }}
            onBlur={(e) => { e.target.style.borderColor = "#E8ECF4"; e.target.style.background = "#F8FAFF"; e.target.style.boxShadow = "none"; }}
        />
    );
}

function SelectField({ value, onChange, children, disabled }) {
    return (
        <select
            value={value}
            onChange={onChange}
            disabled={disabled}
            style={{
                ...fieldBaseStyle,
                cursor: disabled ? "not-allowed" : "pointer",
                background: disabled ? "#F1F5F9" : "#F8FAFF",
                color: disabled ? "#94A3B8" : "#1E293B",
            }}
            onFocus={(e) => { if (!disabled) { e.target.style.borderColor = "#6C3DFF"; e.target.style.background = "#fff"; } }}
            onBlur={(e) => { if (!disabled) { e.target.style.borderColor = "#E8ECF4"; e.target.style.background = "#F8FAFF"; } }}
        >
            {children}
        </select>
    );
}

/* -------------------------------------------------------------------------- */
/*  کمک‌تابع‌های ساختار درختی                                                   */
/* -------------------------------------------------------------------------- */
const normalizeGroup = (raw) => {
    const type = Number(raw.type ?? raw.Type);
    // شناسه/نام آیتمِ متصل (مشتری برای نوع ۱، فروشنده برای نوع ۲، محصول برای نوع ۳)
    let entityId = null;
    let entityName = null;
    if (type === 1) {
        entityId = raw.customerId ?? raw.CustomerId ?? raw.customer?.id ?? raw.Customer?.Id ?? null;
        entityName = raw.customerName ?? raw.CustomerName ?? raw.customer?.name ?? raw.Customer?.Name ?? null;
    } else if (type === 2) {
        entityId = raw.employeeId ?? raw.EmployeeId ?? raw.employee?.id ?? raw.Employee?.Id ?? null;
        entityName = raw.employeeName ?? raw.EmployeeName ?? raw.employee?.name ?? raw.Employee?.Name ?? null;
    } else if (type === 3) {
        entityId = raw.productId ?? raw.ProductId ?? raw.product?.id ?? raw.Product?.Id ?? null;
        entityName = raw.productName ?? raw.ProductName ?? raw.product?.name ?? raw.Product?.Name ?? null;
    }
    return {
        id: raw.id ?? raw.Id,
        name: raw.name ?? raw.Name ?? "",
        type,
        parentId: raw.parentId ?? raw.ParentId ?? null,
        entityId,
        entityName,
    };
};

const normalizeEntity = (raw) => {
    const directName = raw.name ?? raw.Name ?? raw.fullName ?? raw.FullName;
    if (directName) return { id: raw.id ?? raw.Id, name: directName };

    const first = raw.firstName ?? raw.FirstName ?? "";
    const last = raw.lastName ?? raw.LastName ?? "";
    const combined = `${first} ${last}`.trim();

    return {
        id: raw.id ?? raw.Id,
        name: combined || `#${raw.id ?? raw.Id}`, // fallback اگر هیچ‌کدوم نبود
    };
};

function buildTree(list) {
    const map = new Map();
    list.forEach((item) => map.set(item.id, { ...item, children: [] }));
    const roots = [];
    list.forEach((item) => {
        const node = map.get(item.id);
        if (item.parentId && map.has(item.parentId)) {
            map.get(item.parentId).children.push(node);
        } else {
            roots.push(node);
        }
    });
    const sortRec = (nodes) => {
        nodes.sort((a, b) => (a.name || "").localeCompare(b.name || "", "fa"));
        nodes.forEach((n) => sortRec(n.children));
    };
    sortRec(roots);
    return roots;
}

function filterTree(nodes, term) {
    const lower = term.toLowerCase();
    const result = [];
    nodes.forEach((n) => {
        const children = filterTree(n.children, term);
        const selfMatch = (n.name || "").toLowerCase().includes(lower);
        if (selfMatch || children.length > 0) result.push({ ...n, children });
    });
    return result;
}

function collectAllIds(nodes, out = new Set()) {
    nodes.forEach((n) => { out.add(n.id); collectAllIds(n.children, out); });
    return out;
}

function findNodeById(nodes, id) {
    for (const n of nodes) {
        if (n.id === id) return n;
        const found = findNodeById(n.children, id);
        if (found) return found;
    }
    return null;
}

function collectDescendantIds(node, out = new Set()) {
    if (!node) return out;
    out.add(node.id);
    node.children.forEach((c) => collectDescendantIds(c, out));
    return out;
}

function flattenWithDepth(nodes, depth = 0, out = []) {
    nodes.forEach((n) => {
        out.push({ id: n.id, name: n.name, depth, entityId: n.entityId });
        flattenWithDepth(n.children, depth + 1, out);
    });
    return out;
}

/* -------------------------------------------------------------------------- */
/*  خروجی اکسل «آن‌پیوت‌شده»: هر سطح یک ستون جدا، ستون آخر = نام آیتم متصل        */
/*  آیتمِ متصل خودش دیگر یک ردیف/سطح جدا در نظر گرفته نمی‌شود                    */
/* -------------------------------------------------------------------------- */
function flattenForPivotExport(nodes, path = [], out = []) {
    nodes.forEach((n) => {
        if (n.entityId) {
            // آیتم متصل: سطح جدیدی حساب نمی‌شود، فقط در ستون آخر (نام آیتم) می‌آید
            out.push({ path: [...path], entityName: n.name });
        } else {
            const newPath = [...path, n.name];
            if (n.children && n.children.length > 0) {
                flattenForPivotExport(n.children, newPath, out);
            } else {
                // گروه خالی؛ نه زیرگروه دارد و نه آیتمی به آن متصل است
                out.push({ path: newPath, entityName: null });
            }
        }
    });
    return out;
}

/* -------------------------------------------------------------------------- */
/*  کمک‌تابع‌های مربوط به «یک آیتم فقط یک‌بار زیر هر شاخه‌ی اصلی» (روی groups تخت)  */
/* -------------------------------------------------------------------------- */

// از یک گروه شروع می‌کند و تا ریشه (شاخه اصلی) بالا می‌رود
function getRootIdOf(id, flatList) {
    const byId = new Map(flatList.map((g) => [g.id, g]));
    let current = byId.get(id);
    const visited = new Set();
    while (current && current.parentId && byId.has(current.parentId) && !visited.has(current.id)) {
        visited.add(current.id);
        current = byId.get(current.parentId);
    }
    return current ? current.id : id;
}

// تمام entityId هایی که همین حالا، هرجایی زیر یک شاخه‌ی اصلی (root) استفاده شده‌اند
function collectUsedEntityIdsInRoot(rootId, flatList) {
    const childrenByParent = new Map();
    flatList.forEach((g) => {
        const key = g.parentId ?? "root";
        if (!childrenByParent.has(key)) childrenByParent.set(key, []);
        childrenByParent.get(key).push(g);
    });

    const used = new Set();
    const stack = [rootId];
    const visited = new Set();
    while (stack.length) {
        const id = stack.pop();
        if (visited.has(id)) continue;
        visited.add(id);
        (childrenByParent.get(id) || []).forEach((child) => {
            if (child.entityId != null) used.add(child.entityId);
            stack.push(child.id);
        });
    }
    return used;
}

/* -------------------------------------------------------------------------- */
/*  ردیف درخت                                                                  */
/* -------------------------------------------------------------------------- */
const iconBtnStyle = {
    width: 23, height: 23, borderRadius: 7, border: "1px solid #E8ECF4",
    background: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", flexShrink: 0,
};

function TreeRow({ node, depth, expandedSet, onToggle, onAddChild, onAddEntity, onEdit, onDelete, onExportBranch, t, typeMeta, entityMeta }) {
    const [menuOpen, setMenuOpen] = useState(false);
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedSet.has(node.id);
    const childCount = hasChildren ? node.children.length : 0;
    // این گره نمایانگر یک آیتم متصل‌شده (مشتری/فروشنده/محصول) است، نه یک گروه معمولی
    const isEntityLeaf = !!node.entityId;
    // این شاخه مستقیماً حداقل یک آیتم متصل‌شده زیرش دارد؛ پس دیگر نباید بشود زیرگروهِ معمولیِ جدید به آن اضافه کرد
    const hasEntityChild = hasChildren && node.children.some((c) => !!c.entityId);
    // این شاخه حداقل یک زیرگروهِ معمولی (پوشه‌ای، غیر آیتم) دارد؛ پس دیگر نباید بشود مستقیماً به آن آیتم متصل کرد
    const hasFolderChild = hasChildren && node.children.some((c) => !c.entityId);
    const isRoot = depth === 0 && !isEntityLeaf;

    const canAddChild = !isEntityLeaf && !hasEntityChild;
    const canAddEntity = !isEntityLeaf && !hasFolderChild && !!entityMeta;
    const canDelete = !hasChildren;

    useEffect(() => {
        if (!menuOpen) return;
        const close = () => setMenuOpen(false);
        window.addEventListener("click", close);
        return () => window.removeEventListener("click", close);
    }, [menuOpen]);

    return (
        <div>
            <div
                className="grp-row"
                style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "10px 12px", borderRadius: 12, marginRight: depth * 24,
                    border: "1px solid transparent",
                }}
            >
                <button
                    onClick={() => hasChildren && onToggle(node.id)}
                    style={{
                        width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center",
                        background: "none", border: "none", cursor: hasChildren ? "pointer" : "default",
                        transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform .15s",
                        flexShrink: 0, visibility: hasChildren ? "visible" : "hidden",
                    }}
                >
                    <Icon name="chevron-down" size={12} color="#94A3B8" />
                </button>

                <div className="grp-avatar" style={{
                    width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                    background: isEntityLeaf
                        ? `linear-gradient(135deg, ${ENTITY_LEAF_COLOR}1A, ${ENTITY_LEAF_COLOR}33)`
                        : `linear-gradient(135deg, ${typeMeta.color}1A, ${typeMeta.color}33)`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: `1px solid ${isEntityLeaf ? `${ENTITY_LEAF_COLOR}26` : `${typeMeta.color}26`}`,
                }}>
                    {/*
                        نکته‌ی مهم: شکل «لایه‌ها» (گروه‌های معمولی) در همه‌ی سطوح باید کاملاً یکسان باشد (همیشه آیکن پوشه).
                        فقط وقتی گره یک آیتمِ متصل‌شده‌ی واقعی (مشتری/فروشنده/محصول) است، آیکن/رنگ فرق می‌کند.
                    */}
                    <Icon
                        name={isEntityLeaf ? ENTITY_LEAF_ICON : "folder"}
                        size={14}
                        color={isEntityLeaf ? ENTITY_LEAF_COLOR : typeMeta.color}
                    />
                </div>

                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                    <p className="grp-name" style={{
                        margin: 0, fontSize: 12, fontWeight: 700, color: "#1E293B", fontFamily: FONT,
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                        {node.name}
                    </p>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        {!node.parentId && (
                            <span style={{ fontSize: 9, fontWeight: 600, color: "#94A3B8" }}>
                                {t.rootBadge}
                            </span>
                        )}
                        {hasChildren && (
                            <span style={{ fontSize: 9, fontWeight: 600, color: typeMeta.color }}>
                                {!node.parentId && "· "}{childCount} زیرگروه
                            </span>
                        )}
                        {node.entityName && (
                            <span style={{
                                display: "inline-flex", alignItems: "center", gap: 3,
                                fontSize: 9, fontWeight: 600, color: ENTITY_LEAF_COLOR,
                                background: `${ENTITY_LEAF_COLOR}14`, padding: "1px 6px", borderRadius: 99,
                            }}>
                                <Icon name="tag" size={9} color={ENTITY_LEAF_COLOR} />
                                {node.entityName}
                            </span>
                        )}
                    </div>
                </div>

                {/* اکشن‌ها روی دسکتاپ: همه‌ی دکمه‌ها کنار هم، فقط با هاور دیده می‌شوند */}
                <div className="grp-actions-desktop" style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                    {canAddChild && (
                        <button title={t.addChild} onClick={() => onAddChild(node.id)} style={iconBtnStyle}>
                            <Icon name="plus" size={11} color="#6C3DFF" />
                        </button>
                    )}
                    {!isEntityLeaf && hasEntityChild && (
                        <span title={t.addChildDisabledHint} style={{ ...iconBtnStyle, cursor: "not-allowed", opacity: 0.35 }}>
                            <Icon name="lock" size={11} color="#94A3B8" />
                        </span>
                    )}
                    {canAddEntity && (
                        <button title={entityMeta.t.addLabel} onClick={() => onAddEntity(node)} style={iconBtnStyle}>
                            <Icon name="plus" size={11} color={ENTITY_LEAF_COLOR} />
                        </button>
                    )}
                    {!isEntityLeaf && hasFolderChild && entityMeta && (
                        <span title={t.addEntityDisabledHint} style={{ ...iconBtnStyle, cursor: "not-allowed", opacity: 0.35 }}>
                            <Icon name="lock" size={11} color="#94A3B8" />
                        </span>
                    )}
                    {isRoot && onExportBranch && (
                        <button title={t.exportBranch} onClick={() => onExportBranch(node)} style={iconBtnStyle}>
                            <Icon name="download" size={11} color="#10B981" />
                        </button>
                    )}
                    <button title={t.edit} onClick={() => onEdit(node)} style={iconBtnStyle}>
                        <Icon name="edit" size={11} color="#0F172A" />
                    </button>
                    <button
                        title={hasChildren ? t.hasChildrenHint : t.delete}
                        disabled={hasChildren}
                        onClick={() => !hasChildren && onDelete(node)}
                        style={{ ...iconBtnStyle, cursor: hasChildren ? "not-allowed" : "pointer", opacity: hasChildren ? 0.35 : 1 }}
                    >
                        <Icon name="trash" size={11} color="#EF4444" />
                    </button>
                </div>

                {/* اکشن‌ها روی موبایل: یک دکمه‌ی «⋮» که منوی جمع‌وجور باز می‌کند تا جای بیشتری برای نام گروه بماند */}
                <div className="grp-actions-mobile" style={{ position: "relative", flexShrink: 0 }}>
                    <button
                        title={t.edit}
                        onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
                        style={iconBtnStyle}
                    >
                        <Icon name="more" size={13} color="#64748B" />
                    </button>
                    {menuOpen && (
                        <div className="grp-actions-dropdown" onClick={(e) => e.stopPropagation()}>
                            {canAddChild && (
                                <button onClick={() => { setMenuOpen(false); onAddChild(node.id); }}>
                                    <Icon name="plus" size={12} color="#6C3DFF" />{t.addChild}
                                </button>
                            )}
                            {canAddEntity && (
                                <button onClick={() => { setMenuOpen(false); onAddEntity(node); }}>
                                    <Icon name="plus" size={12} color={ENTITY_LEAF_COLOR} />{entityMeta.t.addLabel}
                                </button>
                            )}
                            {isRoot && onExportBranch && (
                                <button onClick={() => { setMenuOpen(false); onExportBranch(node); }}>
                                    <Icon name="download" size={12} color="#10B981" />{t.exportBranch}
                                </button>
                            )}
                            <button onClick={() => { setMenuOpen(false); onEdit(node); }}>
                                <Icon name="edit" size={12} color="#0F172A" />{t.edit}
                            </button>
                            <button
                                disabled={!canDelete}
                                onClick={() => { if (canDelete) { setMenuOpen(false); onDelete(node); } }}
                            >
                                <Icon name="trash" size={12} color={canDelete ? "#EF4444" : "#B0B8C4"} />{t.delete}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {hasChildren && isExpanded && (
                <div style={{ marginRight: 16, borderRight: `2px solid ${typeMeta.color}1F`, paddingRight: 2 }}>
                    {node.children.map((child) => (
                        <TreeRow
                            key={child.id} node={child} depth={depth + 1} expandedSet={expandedSet}
                            onToggle={onToggle} onAddChild={onAddChild} onAddEntity={onAddEntity}
                            onEdit={onEdit} onDelete={onDelete} onExportBranch={onExportBranch}
                            t={t} typeMeta={typeMeta} entityMeta={entityMeta}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  تب‌های نوع گروه‌بندی (بدون باکس مستقل - داخل باکس جستجو/افزودن جای می‌گیرد)  */
/*  همیشه در یک ردیف (grid سه‌ستونه) نمایش داده می‌شوند تا در موبایل هم شکسته نشوند */
/* -------------------------------------------------------------------------- */
function TypeTabs({ activeType, onChange, counts, lang }) {
    return (
        <div className="type-tabs-row">
            {GROUPING_TYPE_OPTIONS.map((opt) => {
                const active = activeType === opt.value;
                return (
                    <button
                        key={opt.value}
                        onClick={() => onChange(opt.value)}
                        className="type-tab-btn"
                        style={{
                            background: active ? `linear-gradient(135deg, ${opt.color}, ${opt.color}CC)` : "transparent",
                            color: active ? "#fff" : "#64748B",
                            boxShadow: active ? `0 4px 10px ${opt.color}40` : "none",
                        }}
                    >
                        <span className="type-tab-icon" style={{
                            background: active ? "rgba(255,255,255,0.22)" : `${opt.color}14`,
                        }}>
                            <Icon name={opt.icon} size={10} color={active ? "#fff" : opt.color} />
                        </span>
                        <span className="type-tab-label">{opt.label[lang]}</span>
                        <span className="type-tab-count" style={{
                            background: active ? "rgba(255,255,255,0.25)" : "#F1F5F9",
                            color: active ? "#fff" : "#94A3B8",
                        }}>
                            {counts[opt.value] ?? 0}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  انتخاب‌گر آیتم (مشتری/فروشنده/محصول) - داخل مودالِ «افزودن به گروه»          */
/* -------------------------------------------------------------------------- */
function EntityPicker({ entities, loading, value, onChange, meta, usedEntityIds, t }) {
    const [term, setTerm] = useState("");

    const filtered = useMemo(() => {
        if (!term.trim()) return entities;
        const lower = term.toLowerCase();
        return entities.filter((e) => (e.name || "").toLowerCase().includes(lower));
    }, [entities, term]);

    const selected = entities.find((e) => e.id === value) || null;

    if (loading) {
        return (
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "#94A3B8", padding: "8px 0" }}>
                <div style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid #EDE9FF", borderTopColor: "#6C3DFF", animation: "spin .8s linear infinite" }} />
                {meta.t.loading}
            </div>
        );
    }

    return (
        <div>
            <p style={{ fontSize: 10, color: "#94A3B8", margin: "0 0 8px" }}>{meta.t.selectHint}</p>
            <div style={{ position: "relative", marginBottom: 8 }}>
                <input
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    placeholder={meta.t.placeholder}
                    style={{ ...fieldBaseStyle, padding: "0 36px 0 12px" }}
                />
                <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}>
                    <Icon name="search" size={13} color="#94A3B8" />
                </span>
            </div>

            <div style={{
                maxHeight: 200, overflowY: "auto", border: "1.5px solid #E8ECF4", borderRadius: 10,
                background: "#F8FAFF",
            }}>
                {filtered.length === 0 ? (
                    <p style={{ margin: 0, padding: "10px 12px", fontSize: 11, color: "#94A3B8" }}>{meta.t.empty}</p>
                ) : (
                    filtered.map((e) => {
                        const isUsed = usedEntityIds?.has(e.id);
                        return (
                            <button
                                key={e.id}
                                type="button"
                                disabled={isUsed}
                                onClick={() => !isUsed && onChange(e.id)}
                                style={{
                                    width: "100%", textAlign: "right", padding: "9px 12px", border: "none",
                                    borderTop: "1px solid #EEF2FF",
                                    background: isUsed ? "#F8FAFC" : value === e.id ? "#EDE9FF" : "transparent",
                                    cursor: isUsed ? "not-allowed" : "pointer",
                                    fontFamily: FONT, fontSize: 11.5, color: isUsed ? "#B0B8C4" : "#1E293B",
                                    fontWeight: value === e.id ? 700 : 500,
                                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                                }}
                            >
                                <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.name}</span>
                                    {isUsed && (
                                        <span style={{
                                            fontSize: 9, fontWeight: 600, color: "#94A3B8",
                                            background: "#EEF2F6", padding: "1px 6px", borderRadius: 99, flexShrink: 0,
                                        }}>
                                            {t?.entityAlreadyUsed}
                                        </span>
                                    )}
                                </span>
                                {value === e.id && !isUsed && <Icon name="check" size={12} color="#6C3DFF" />}
                            </button>
                        );
                    })
                )}
            </div>

            {selected && (
                <div style={{
                    marginTop: 8, display: "inline-flex", alignItems: "center", gap: 5,
                    fontSize: 10, fontWeight: 600, color: ENTITY_LEAF_COLOR, background: `${ENTITY_LEAF_COLOR}14`,
                    padding: "3px 9px", borderRadius: 99,
                }}>
                    <Icon name="tag" size={10} color={ENTITY_LEAF_COLOR} />
                    {selected.name}
                </div>
            )}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  کامپوننت اصلی                                                              */
/* -------------------------------------------------------------------------- */
export default function GroupingManagement() {
    const { lang } = useLanguage();
    const t = TEXTS[lang] || TEXTS.fa;

    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState({ visible: false, message: "", type: "success" });
    const [expanded, setExpanded] = useState(new Set());
    const [searchTerm, setSearchTerm] = useState("");
    const [activeType, setActiveType] = useState(GROUPING_TYPE_OPTIONS[0].value); // تب فعال بر اساس Enum
    const [modal, setModal] = useState(null); // { mode: 'add' | 'edit', loadingData, data:{id,name,type,parentId} }
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exportingBranchId, setExportingBranchId] = useState(null);

    // کش آیتم‌های قابل‌اتصال (مشتری/فروشنده/محصول) به تفکیک نوع گروه‌بندی - تنبل (lazy) بارگذاری می‌شود
    const [entitiesCache, setEntitiesCache] = useState({});
    const [entitiesLoading, setEntitiesLoading] = useState({});
    const [attachModal, setAttachModal] = useState(null); // { parentId, type, selectedId, usedEntityIds }
    const [attaching, setAttaching] = useState(false);

    const showToast = (message, type = "success") => {
        setToast({ visible: true, message, type });
        setTimeout(() => setToast({ visible: false, message: "", type: "success" }), 3000);
    };
    const extractServerErrorMessage = (err) => {
        const data = err?.response?.data;
        if (typeof data === "string" && data.trim()) return data.trim();
        if (data && typeof data === "object") {
            return data.message || data.Message || data.title || data.error || null;
        }
        return null;
    };
    const fetchGroups = async () => {
        try {
            const res = await GetAllGrouping();
            const raw = res.data?.data ?? res.data ?? [];
            setGroups(Array.isArray(raw) ? raw.map(normalizeGroup) : []);
        } catch (err) {
            showToast(extractServerErrorMessage(err) || t.toastFetchFail, "error");
        }
    };

    useEffect(() => {
        (async () => {
            setLoading(true);
            await fetchGroups();
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const ensureEntitiesLoaded = async (type) => {
        if (entitiesCache[type] || entitiesLoading[type]) return;
        const meta = getEntityMeta(type, lang);
        if (!meta) return;
        setEntitiesLoading((prev) => ({ ...prev, [type]: true }));
        try {
            const res = await meta.fetch();
            const raw = res.data?.data ?? res.data ?? [];
            setEntitiesCache((prev) => ({ ...prev, [type]: Array.isArray(raw) ? raw.map(normalizeEntity) : [] }));
        } catch (err) {
            showToast(extractServerErrorMessage(err) || meta.t.toastFetchFail, "error");
        } finally {
            setEntitiesLoading((prev) => ({ ...prev, [type]: false }));
        }
    };

    // تعداد گروه‌ها به تفکیک هر نوع، برای نمایش روی تب‌ها
    const countsByType = useMemo(() => {
        const c = {};
        groups.forEach((g) => { c[g.type] = (c[g.type] || 0) + 1; });
        return c;
    }, [groups]);

    // فقط گروه‌های متعلق به تب فعال، سپس ساخت درخت از همون زیرمجموعه
    const groupsInActiveType = useMemo(
        () => groups.filter((g) => Number(g.type) === activeType),
        [groups, activeType]
    );
    const tree = useMemo(() => buildTree(groupsInActiveType), [groupsInActiveType]);
    const visibleTree = useMemo(
        () => (searchTerm.trim() ? filterTree(tree, searchTerm.trim()) : tree),
        [tree, searchTerm]
    );

    useEffect(() => {
        if (searchTerm.trim()) {
            const ids = collectAllIds(filterTree(tree, searchTerm.trim()));
            setExpanded((prev) => new Set([...prev, ...ids]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    // با تغییر تب، جستجو ریست شود تا نتیجه‌ی تب قبلی گمراه‌کننده نباشد
    const handleChangeType = (value) => {
        setActiveType(value);
        setSearchTerm("");
    };

    const toggleExpand = (id) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const openAddModal = (parentId = null) => {
        const parentNode = parentId ? findNodeById(tree, parentId) : null;
        // نوع همیشه از تب فعال (یا والد، که خودش هم‌نوع تب فعال است) گرفته می‌شود
        const initialType = parentNode ? parentNode.type : activeType;
        setModal({ mode: "add", loadingData: false, data: { id: null, name: "", type: initialType, parentId } });
        if (parentId) setExpanded((prev) => new Set([...prev, parentId]));
    };

    const openEditModal = async (node) => {
        setModal({ mode: "edit", loadingData: true, data: { id: node.id, name: node.name, type: node.type, parentId: node.parentId } });
        try {
            const res = await GetGroupingById(node.id);
            const raw = res.data?.data ?? res.data;
            if (raw) {
                const fresh = normalizeGroup(raw);
                setModal({
                    mode: "edit",
                    loadingData: false,
                    data: {
                        id: node.id, // همیشه از گره‌ی اصلی؛ اگر پاسخ سرور id را برنگرداند جابه‌جا نشود
                        name: fresh.name || node.name,
                        type: fresh.type ?? node.type,
                        parentId: fresh.parentId !== undefined ? fresh.parentId : node.parentId,
                    },
                });
            } else {
                setModal((m) => (m ? { ...m, loadingData: false } : m));
            }
        } catch (err) {
            showToast(extractServerErrorMessage(err) || t.toastFetchFail, "error");
            setModal(null);
        }
    };

    // بستن مودال فقط از طریق دکمه‌های انصراف/×  - کلیک روی نقاط دیگر صفحه آن را نمی‌بندد
    const closeModal = () => { if (!saving) setModal(null); };

    const handleSubmit = async () => {
        if (!modal) return;
        const name = modal.data.name?.trim();
        if (!name) { showToast(t.toastNameRequired, "error"); return; }

        const payload = modal.mode === "add"
            ? { name, type: Number(modal.data.type), parentId: modal.data.parentId || null }
            : { id: modal.data.id, name, type: Number(modal.data.type), parentId: modal.data.parentId || null };

        setSaving(true);
        try {
            if (modal.mode === "add") {
                await AddGrouping(payload);
                showToast(t.toastAddSuccess);
            } else {
                await UpdateGrouping(payload);
                showToast(t.toastUpdateSuccess);
            }
            setModal(null);
            await fetchGroups();
        } catch (err) {
            showToast(extractServerErrorMessage(err) || (modal.mode === "add" ? t.toastAddFail : t.toastUpdateFail), "error");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await RemoveGrouping(deleteTarget.id);
            showToast(t.toastDeleteSuccess);
            setDeleteTarget(null);
            await fetchGroups();
        } catch (err) {
            showToast(extractServerErrorMessage(err) || t.toastDeleteFail, "error");
        } finally {
            setDeleting(false);
        }
    };

    const handleSearchLookup = async () => {
        const term = searchTerm.trim();
        if (!term) return;
        try {
            const res = await GetGroupingByName(term);
            const raw = res.data?.data ?? res.data;
            if (!raw) { showToast(t.toastSearchNotFound, "error"); return; }
            const found = normalizeGroup(raw);
            // اگر گروه پیدا شده متعلق به تب دیگری بود، خودکار به همان تب برو
            if (Number(found.type) !== activeType) setActiveType(Number(found.type));
            const ids = new Set(expanded);
            let current = found;
            while (current) {
                ids.add(current.id);
                current = groups.find((g) => g.id === current.parentId) || null;
            }
            setExpanded(ids);
            showToast(t.toastSearchFound);
        } catch (err) {
            showToast(extractServerErrorMessage(err) || t.toastSearchFail, "error");
        }
    };

    // باز کردن مودال «افزودن آیتم» (مشتری/فروشنده/محصول بسته به نوع تب فعال) زیر یک گره مشخص
    const openAttachModal = (node) => {
        // شاخه‌ی اصلی (root) که این گره زیرش قرار دارد، برای محدود کردن آیتم‌های تکراری در کل آن شاخه
        const rootId = getRootIdOf(node.id, groups);
        const usedEntityIds = collectUsedEntityIdsInRoot(rootId, groups);
        setAttachModal({ parentId: node.id, type: node.type, selectedId: null, usedEntityIds });
        ensureEntitiesLoaded(node.type);
        setExpanded((prev) => new Set([...prev, node.id]));
    };
    const closeAttachModal = () => { if (!attaching) setAttachModal(null); };

    const handleAttachSubmit = async () => {
        if (!attachModal || !attachModal.selectedId) return;
        const meta = getEntityMeta(attachModal.type, lang);
        const list = entitiesCache[attachModal.type] || [];
        const entity = list.find((e) => e.id === attachModal.selectedId);
        if (!entity || !meta) return;

        // بررسی نهایی سمت فرانت قبل از ارسال (خط دفاعی اول - خط دفاعی اصلی سمت بک‌اند است)
        if (attachModal.usedEntityIds?.has(entity.id)) {
            showToast(t.toastAttachDuplicate, "error");
            return;
        }

        setAttaching(true);
        try {
            const payload = {
                name: entity.name,
                type: Number(attachModal.type),
                parentId: attachModal.parentId,
                [meta.idField]: entity.id,
            };
            await AddGrouping(payload);
            showToast(meta.t.toastSuccess);
            setAttachModal(null);
            await fetchGroups();
        } catch (err) {
            const status = err?.response?.status;
            if (status === 409 || status === 400) {
                showToast(t.toastAttachDuplicate, "error");
            } else {
                showToast(extractServerErrorMessage(err) || meta.t.toastFail, "error");
            }
        } finally {
            setAttaching(false);
        }
    };

    // گزینه‌های والد برای فرم: فقط از همان تب فعال، بدون خودِ گره و زیرمجموعه‌هایش (هنگام ویرایش)،
    // و بدون آیتم‌های متصل‌شده (چون امکان داشتن زیرمجموعه را ندارند)
    const parentOptions = useMemo(() => {
        const excluded = modal?.mode === "edit" ? collectDescendantIds(findNodeById(tree, modal.data.id)) : new Set();
        return flattenWithDepth(tree).filter((n) => !excluded.has(n.id) && !n.entityId);
    }, [tree, modal]);

    const activeTypeMeta = getTypeMeta(activeType);
    const activeEntityMeta = getEntityMeta(activeType, lang);

    /* ---------------------------------------------------------------------- */
    /*  موتور مشترک ساخت و دانلود اکسل (هم برای کل تب، هم برای یک شاخه اصلی)     */
    /*  ساختار: به تعداد سطوح واقعی درخت، ستون جدا + یک ستون آخر برای نام آیتم   */
    /* ---------------------------------------------------------------------- */
    const buildAndDownloadExcel = async (rows, { sheetLabel, fileLabel, color, maxLevels, entityColumnLabel }) => {
        const workbook = new ExcelJS.Workbook();
        workbook.creator = "Ordering System";
        workbook.created = new Date();

        const sheetName = (sheetLabel || "Sheet").slice(0, 31);
        const sheet = workbook.addWorksheet(sheetName, {
            views: [{ rightToLeft: true, state: "frozen", ySplit: 1 }],
        });

        const levelColumns = Array.from({ length: maxLevels }, (_, i) => ({
            header: `${t.colLevel} ${i + 1}`,
            key: `level${i + 1}`,
            width: 24,
        }));

        sheet.columns = [
            { header: t.colIndex, key: "index", width: 8 },
            ...levelColumns,
            { header: entityColumnLabel, key: "entity", width: 30 },
        ];

        rows.forEach((r, idx) => {
            const rowData = { index: idx + 1, entity: r.entityName || "-" };
            r.path.forEach((name, i) => { rowData[`level${i + 1}`] = name; });
            sheet.addRow(rowData);
        });

        const brandArgb = `FF${color.replace("#", "")}`;

        // استایل هدر
        const headerRow = sheet.getRow(1);
        headerRow.height = 26;
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: brandArgb } };
            cell.alignment = { vertical: "middle", horizontal: "center" };
            cell.border = { bottom: { style: "thin", color: { argb: "FFDDDDDD" } } };
        });

        // استایل بدنه (زبرا + بوردر ملایم)
        sheet.eachRow((row, rowNumber) => {
            if (rowNumber === 1) return;
            row.height = 20;
            row.eachCell((cell) => {
                cell.font = { size: 10.5 };
                cell.alignment = { vertical: "middle", horizontal: "center" };
                cell.border = {
                    top: { style: "thin", color: { argb: "FFF1F1F1" } },
                    bottom: { style: "thin", color: { argb: "FFF1F1F1" } },
                };
                if (rowNumber % 2 === 0) {
                    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF8FAFF" } };
                }
            });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const dateStr = new Date().toISOString().slice(0, 10);
        saveAs(blob, `${fileLabel}-${dateStr}.xlsx`);
    };

    /* ---------------------------------------------------------------------- */
    /*  خروجی اکسل شیک و تمیز برای تب/دسته‌ی فعال                               */
    /* ---------------------------------------------------------------------- */
    const handleExportExcel = async () => {
        const rows = flattenForPivotExport(tree);
        if (rows.length === 0) {
            showToast(t.toastExportEmpty, "error");
            return;
        }
        const maxLevels = Math.max(...rows.map((r) => r.path.length), 1);

        setExporting(true);
        try {
            await buildAndDownloadExcel(rows, {
                sheetLabel: activeTypeMeta.label[lang],
                fileLabel: activeTypeMeta.label[lang],
                color: activeTypeMeta.color,
                maxLevels,
                entityColumnLabel: ENTITY_COLUMN_LABEL[activeType]?.[lang] || t.colName,
            });
            showToast(t.toastExportSuccess);
        } catch {
            showToast(t.toastExportFail, "error");
        } finally {
            setExporting(false);
        }
    };

    /* ---------------------------------------------------------------------- */
    /*  خروجی اکسل مخصوص یک شاخه‌ی اصلی (root) به همراه تمام زیرمجموعه‌هایش      */
    /* ---------------------------------------------------------------------- */
    const handleExportBranch = async (node) => {
        const rows = flattenForPivotExport([node]);
        if (rows.length === 0) {
            showToast(t.toastExportEmpty, "error");
            return;
        }
        const maxLevels = Math.max(...rows.map((r) => r.path.length), 1);

        setExportingBranchId(node.id);
        try {
            await buildAndDownloadExcel(rows, {
                sheetLabel: node.name,
                fileLabel: node.name,
                color: activeTypeMeta.color,
                maxLevels,
                entityColumnLabel: ENTITY_COLUMN_LABEL[activeType]?.[lang] || t.colName,
            });
            showToast(t.toastExportSuccess);
        } catch {
            showToast(t.toastExportFail, "error");
        } finally {
            setExportingBranchId(null);
        }
    };

    return (
        <div style={{ fontFamily: FONT, direction: t.dir, minHeight: "100vh", background: "#F5F6FA" }}>
            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes fadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
                .grp-row { transition: background .15s, border-color .15s; }
                .grp-row:hover { background: #F8FAFF; border-color: #EEF2FF !important; }
                .grp-actions-desktop { opacity: 0; transition: opacity .15s; }
                .grp-row:hover .grp-actions-desktop { opacity: 1; }
                .icon-btn:hover:not(:disabled) { background: #F1F5F9 !important; }
                .primary-btn:hover:not(:disabled) { transform: translateY(-1px); }

                /* --- تب‌های نوع گروه‌بندی: همیشه یک ردیف سه‌ستونه، هرگز شکسته نمی‌شود --- */
                .type-tabs-row {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 4px;
                    width: 100%;
                }
                .type-tab-btn {
                    display: flex; align-items: center; justify-content: center; gap: 6px;
                    padding: 7px 10px; border-radius: 9px; border: none; cursor: pointer;
                    font-family: inherit; font-size: 11px; font-weight: 600;
                    transition: all .18s; min-width: 0;
                }
                .type-tab-icon {
                    display: flex; align-items: center; justify-content: center;
                    width: 18px; height: 18px; border-radius: 6px; flex-shrink: 0;
                }
                .type-tab-label {
                    overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0;
                }
                .type-tab-count {
                    font-size: 9px; font-weight: 700; padding: 1px 6px; border-radius: 99px; flex-shrink: 0;
                }

                /* --- اکشن‌های ردیف درخت: دسکتاپ = دکمه‌های کنار هم، موبایل = منوی «⋮» --- */
                .grp-actions-mobile { display: none; }
                .grp-actions-dropdown {
                    position: absolute; top: calc(100% + 4px); left: 0;
                    background: #fff; border: 1px solid #E8ECF4; border-radius: 10px;
                    box-shadow: 0 10px 28px rgba(15,23,42,.14); z-index: 60;
                    display: flex; flex-direction: column; min-width: 168px; padding: 4px; gap: 1px;
                }
                .grp-actions-dropdown button {
                    display: flex; align-items: center; gap: 8px; padding: 9px 10px; border: none;
                    background: none; border-radius: 8px; font-family: inherit; font-size: 11.5px;
                    cursor: pointer; text-align: right; color: #1E293B; white-space: nowrap;
                }
                .grp-actions-dropdown button:hover:not(:disabled) { background: #F8FAFF; }
                .grp-actions-dropdown button:disabled { color: #B0B8C4; cursor: not-allowed; }

                @media (max-width: 520px) {
                    .grp-actions-desktop { display: none !important; }
                    .grp-actions-mobile { display: block; }
                    .grp-avatar { width: 28px !important; height: 28px !important; }
                    .grp-name { font-size: 11.5px !important; }
                    .type-tab-btn { padding: 6px 4px; font-size: 9.5px; gap: 4px; }
                    .type-tab-icon { width: 15px; height: 15px; }
                    .type-tab-count { font-size: 8px; padding: 1px 4px; }
                }
                @media (max-width: 380px) {
                    .type-tab-label { display: none; }
                    .type-tab-btn { padding: 8px 4px; }
                }
            `}</style>

            {/* هدر */}
            <div style={{
                background: "linear-gradient(135deg, #6C3DFF 0%, #9B59FF 100%)",
                padding: "26px 28px 84px", position: "relative", overflow: "hidden",
            }}>
                <div style={{ position: "absolute", top: -60, left: -60, width: 220, height: 220, borderRadius: "50%", background: "rgba(255,255,255,0.06)" }} />
                <div style={{ position: "absolute", bottom: -40, right: 80, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
                <h1 style={{ fontSize: 19, fontWeight: 700, color: "#fff", margin: 0 }}>{t.title}</h1>
                <p style={{ fontSize: 11, color: "rgba(255,255,255,0.65)", margin: "4px 0 0" }}>{t.subtitle}</p>
            </div>

            {/*
              باکس یکپارچه‌ی تب‌های نوع + جستجو + افزودن گروه ریشه
              نیمی از این باکس روی هدر بنفش و نیمی دیگر روی کارت زیرین قرار می‌گیرد
            */}
            <div style={{ padding: "0 28px", marginTop: -56, animation: "fadeUp .4s ease both", position: "relative", zIndex: 2 }}>
                <div style={{
                    background: "#fff", borderRadius: 18,
                    border: "1px solid #EEF2FF", boxShadow: "0 8px 28px rgba(108,61,255,0.10)",
                    overflow: "hidden",
                }}>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", padding: "10px 14px", borderBottom: "1px solid #F1F5F9" }}>
                        <TypeTabs activeType={activeType} onChange={handleChangeType} counts={countsByType} lang={lang} />
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", flexWrap: "wrap" }}>
                        <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
                            <input
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleSearchLookup()}
                                placeholder={t.searchPlaceholder}
                                style={{ ...fieldBaseStyle, padding: "0 36px 0 12px" }}
                            />
                            <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}>
                                <Icon name="search" size={13} color="#94A3B8" />
                            </span>
                        </div>

                        <span style={{ fontSize: 10, color: "#94A3B8", whiteSpace: "nowrap" }}>{t.itemsCount(groupsInActiveType.length)}</span>

                        <button
                            className="primary-btn"
                            onClick={handleExportExcel}
                            disabled={exporting}
                            title={t.exportExcel}
                            style={{
                                height: 40, background: "#fff", border: "1.5px solid #E8ECF4", borderRadius: 10, color: "#1E293B",
                                fontSize: 11.5, fontFamily: FONT, fontWeight: 600, cursor: exporting ? "not-allowed" : "pointer",
                                padding: "0 14px", display: "flex", alignItems: "center", gap: 6,
                                whiteSpace: "nowrap", transition: "all .2s", opacity: exporting ? 0.6 : 1,
                            }}
                        >
                            {exporting ? (
                                <div style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid #D1FAE5", borderTopColor: "#10B981", animation: "spin .7s linear infinite" }} />
                            ) : (
                                <Icon name="download" size={13} color="#10B981" />
                            )}
                            {t.exportExcel}
                        </button>

                        <button
                            className="primary-btn"
                            onClick={() => openAddModal(null)}
                            style={{
                                height: 40, background: activeTypeMeta.color, border: "none", borderRadius: 10, color: "#fff",
                                fontSize: 11.5, fontFamily: FONT, fontWeight: 600, cursor: "pointer",
                                padding: "0 16px", display: "flex", alignItems: "center", gap: 6,
                                boxShadow: `0 4px 12px ${activeTypeMeta.color}40`, whiteSpace: "nowrap", transition: "all .2s",
                            }}
                        >
                            <Icon name="plus" size={13} color="#fff" />
                            {t.addRoot}
                        </button>
                    </div>
                </div>
            </div>

            {/* درخت گروه‌ها (فقط تب فعال) */}
            <div style={{ padding: "18px 28px 40px", animation: "fadeUp .5s .1s ease both" }}>
                <div style={{
                    background: "#fff", borderRadius: 18, border: "1px solid #EEF2FF",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(108,61,255,0.04)",
                    padding: "14px 12px",
                }}>
                    {loading ? (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "56px 0" }}>
                            <div style={{ width: 36, height: 36, borderRadius: "50%", border: "3px solid #EDE9FF", borderTopColor: "#6C3DFF", animation: "spin .8s linear infinite", marginBottom: 12 }} />
                            <p style={{ color: "#94A3B8", fontSize: 12 }}>{t.loading}</p>
                        </div>
                    ) : visibleTree.length === 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "46px 0", gap: 12 }}>
                            <div style={{
                                width: 48, height: 48, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center",
                                background: `${activeTypeMeta.color}14`,
                            }}>
                                <Icon name={activeTypeMeta.icon} size={20} color={activeTypeMeta.color} />
                            </div>
                            <p style={{ color: "#94A3B8", fontSize: 12, margin: 0 }}>
                                {searchTerm.trim() ? t.noSearchResult : t.empty}
                            </p>
                            {!searchTerm.trim() && (
                                <button
                                    onClick={() => openAddModal(null)}
                                    style={{
                                        background: `${activeTypeMeta.color}14`, color: activeTypeMeta.color, border: "none", borderRadius: 10,
                                        padding: "7px 16px", fontSize: 11, fontWeight: 600, cursor: "pointer",
                                        fontFamily: FONT,
                                    }}
                                >
                                    {t.emptyAction}
                                </button>
                            )}
                        </div>
                    ) : (
                        visibleTree.map((node) => (
                            <TreeRow
                                key={node.id} node={node} depth={0} expandedSet={expanded}
                                onToggle={toggleExpand} onAddChild={openAddModal} onAddEntity={openAttachModal}
                                onEdit={openEditModal} onDelete={setDeleteTarget} onExportBranch={handleExportBranch}
                                t={t} typeMeta={activeTypeMeta} entityMeta={activeEntityMeta}
                            />
                        ))
                    )}
                </div>
            </div>

            {/* مودال افزودن / ویرایش گروه - با کلیک روی پس‌زمینه بسته نمی‌شود */}
            {modal && (
                <div
                    style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1500, padding: 16 }}
                >
                    <div
                        style={{ background: "#fff", borderRadius: 18, width: "min(400px, 100%)", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(15,23,42,.25)" }}
                    >
                        <div style={{ padding: "16px 20px", borderBottom: "1px solid #F1F5F9", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                                <span style={{
                                    width: 26, height: 26, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
                                    background: `${getTypeMeta(modal.data.type).color}14`,
                                }}>
                                    <Icon name={getTypeMeta(modal.data.type).icon} size={12} color={getTypeMeta(modal.data.type).color} />
                                </span>
                                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#1E293B" }}>
                                    {modal.mode === "add" ? t.modalAddTitle : t.modalEditTitle}
                                </p>
                            </div>
                            <button onClick={closeModal} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
                                <Icon name="close" size={16} color="#94A3B8" />
                            </button>
                        </div>

                        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
                            {modal.loadingData ? (
                                <div style={{ display: "flex", justifyContent: "center", padding: "18px 0" }}>
                                    <div style={{ width: 26, height: 26, borderRadius: "50%", border: "3px solid #EDE9FF", borderTopColor: "#6C3DFF", animation: "spin .8s linear infinite" }} />
                                </div>
                            ) : (
                                <>
                                    {/* نوع دیگر دراپ‌داون نیست؛ از تب فعال/والد مشخص می‌شود و فقط به‌صورت بج نمایش داده می‌شود */}
                                    <div style={{
                                        display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "#64748B",
                                        background: "#F8FAFF", border: "1px solid #EEF2FF", borderRadius: 9, padding: "7px 10px",
                                    }}>
                                        {t.categoryHint}
                                        <span style={{
                                            fontSize: 10, fontWeight: 700, padding: "2px 9px", borderRadius: 99,
                                            background: `${getTypeMeta(modal.data.type).color}14`, color: getTypeMeta(modal.data.type).color,
                                        }}>
                                            {getTypeMeta(modal.data.type).label[lang]}
                                        </span>
                                        {t.categoryHintSuffix}
                                    </div>

                                    <div>
                                        <label style={{ fontSize: 10, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>{t.name}</label>
                                        <TextField
                                            autoFocus
                                            value={modal.data.name}
                                            onChange={(e) => setModal((m) => ({ ...m, data: { ...m.data, name: e.target.value } }))}
                                            placeholder={t.name}
                                        />
                                    </div>

                                    <div>
                                        <label style={{ fontSize: 10, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>{t.parent}</label>
                                        {modal.mode === "add" ? (
                                            <div style={{
                                                ...fieldBaseStyle,
                                                display: "flex", alignItems: "center",
                                                background: "#F1F5F9", color: "#64748B", cursor: "not-allowed",
                                            }}>
                                                {modal.data.parentId ? (findNodeById(tree, modal.data.parentId)?.name ?? t.none) : t.none}
                                            </div>
                                        ) : (
                                            <SelectField
                                                value={modal.data.parentId ?? ""}
                                                onChange={(e) => {
                                                    const newParentId = e.target.value ? Number(e.target.value) : null;
                                                    setModal((m) => ({ ...m, data: { ...m.data, parentId: newParentId } }));
                                                }}
                                            >
                                                <option value="">{t.none}</option>
                                                {parentOptions.map((opt) => (
                                                    <option key={opt.id} value={opt.id}>
                                                        {"— ".repeat(opt.depth)}{opt.name}
                                                    </option>
                                                ))}
                                            </SelectField>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>

                        <div style={{ padding: "14px 20px", borderTop: "1px solid #F1F5F9", display: "flex", justifyContent: "flex-end", gap: 8, position: "sticky", bottom: 0, background: "#fff" }}>
                            <button
                                onClick={closeModal}
                                disabled={saving}
                                style={{
                                    height: 38, padding: "0 16px", borderRadius: 10, border: "1.5px solid #E8ECF4",
                                    background: "#fff", color: "#475569", fontSize: 11.5, fontWeight: 600,
                                    fontFamily: FONT, cursor: "pointer",
                                }}
                            >
                                {t.cancel}
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={saving || modal.loadingData}
                                style={{
                                    height: 38, padding: "0 20px", borderRadius: 10, border: "none",
                                    background: saving ? "#A78BFA" : "#6C3DFF", color: "#fff", fontSize: 11.5, fontWeight: 600,
                                    fontFamily: FONT, cursor: saving ? "not-allowed" : "pointer",
                                    display: "flex", alignItems: "center", gap: 8,
                                }}
                            >
                                {saving && <div style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", animation: "spin .7s linear infinite" }} />}
                                {saving ? t.saving : t.save}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* مودال «افزودن آیتم به گروه» (مشتری/فروشنده/محصول بسته به نوع تب فعال) */}
            {attachModal && (
                <div
                    style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1550, padding: 16 }}
                >
                    <div
                        style={{ background: "#fff", borderRadius: 18, width: "min(400px, 100%)", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(15,23,42,.25)" }}
                    >
                        <div style={{ padding: "16px 20px", borderBottom: "1px solid #F1F5F9", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                                <span style={{
                                    width: 26, height: 26, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
                                    background: `${ENTITY_LEAF_COLOR}14`,
                                }}>
                                    <Icon name="tag" size={12} color={ENTITY_LEAF_COLOR} />
                                </span>
                                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#1E293B" }}>
                                    {getEntityMeta(attachModal.type, lang)?.t.modalTitle}
                                </p>
                            </div>
                            <button onClick={closeAttachModal} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
                                <Icon name="close" size={16} color="#94A3B8" />
                            </button>
                        </div>

                        <div style={{ padding: 20 }}>
                            <EntityPicker
                                entities={entitiesCache[attachModal.type] || []}
                                loading={!!entitiesLoading[attachModal.type]}
                                value={attachModal.selectedId}
                                onChange={(id) => setAttachModal((m) => ({ ...m, selectedId: id }))}
                                meta={getEntityMeta(attachModal.type, lang)}
                                usedEntityIds={attachModal.usedEntityIds}
                                t={t}
                            />
                        </div>

                        <div style={{ padding: "14px 20px", borderTop: "1px solid #F1F5F9", display: "flex", justifyContent: "flex-end", gap: 8, position: "sticky", bottom: 0, background: "#fff" }}>
                            <button
                                onClick={closeAttachModal}
                                disabled={attaching}
                                style={{
                                    height: 38, padding: "0 16px", borderRadius: 10, border: "1.5px solid #E8ECF4",
                                    background: "#fff", color: "#475569", fontSize: 11.5, fontWeight: 600,
                                    fontFamily: FONT, cursor: "pointer",
                                }}
                            >
                                {t.cancel}
                            </button>
                            <button
                                onClick={handleAttachSubmit}
                                disabled={attaching || !attachModal.selectedId}
                                style={{
                                    height: 38, padding: "0 20px", borderRadius: 10, border: "none",
                                    background: !attachModal.selectedId ? "#99E6DD" : attaching ? "#5EEAD4" : ENTITY_LEAF_COLOR,
                                    color: "#fff", fontSize: 11.5, fontWeight: 600,
                                    fontFamily: FONT, cursor: attaching || !attachModal.selectedId ? "not-allowed" : "pointer",
                                    display: "flex", alignItems: "center", gap: 8,
                                }}
                            >
                                {attaching && <div style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", animation: "spin .7s linear infinite" }} />}
                                {getEntityMeta(attachModal.type, lang)?.t.addLabel}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* مودال تایید حذف - با کلیک روی پس‌زمینه بسته نمی‌شود */}
            {deleteTarget && (
                <div
                    style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1600, padding: 16 }}
                >
                    <div
                        style={{ background: "#fff", borderRadius: 18, width: "min(360px, 100%)", padding: 20, boxShadow: "0 20px 60px rgba(15,23,42,.25)" }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                            <div style={{ width: 36, height: 36, borderRadius: 10, background: "#FEF2F2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <Icon name="trash" size={15} color="#EF4444" />
                            </div>
                            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#1E293B" }}>{t.confirmDeleteTitle}</p>
                        </div>
                        <p style={{ fontSize: 12, color: "#64748B", margin: "0 0 18px", lineHeight: 1.7 }}>
                            {t.confirmDeleteMsg(deleteTarget.name)}
                        </p>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                            <button
                                onClick={() => setDeleteTarget(null)}
                                disabled={deleting}
                                style={{
                                    height: 38, padding: "0 16px", borderRadius: 10, border: "1.5px solid #E8ECF4",
                                    background: "#fff", color: "#475569", fontSize: 11.5, fontWeight: 600,
                                    fontFamily: FONT, cursor: "pointer",
                                }}
                            >
                                {t.cancelDelete}
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={deleting}
                                style={{
                                    height: 38, padding: "0 18px", borderRadius: 10, border: "none",
                                    background: "#EF4444", color: "#fff", fontSize: 11.5, fontWeight: 600,
                                    fontFamily: FONT, cursor: deleting ? "not-allowed" : "pointer",
                                    display: "flex", alignItems: "center", gap: 8,
                                }}
                            >
                                {deleting && <div style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", animation: "spin .7s linear infinite" }} />}
                                {deleting ? t.deleting : t.yesDelete}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Toast message={toast.message} visible={toast.visible} type={toast.type} />
        </div>
    );
}